const apiVersion = 'v1';
const isDev = process.env.NODE_ENV !== 'production';

// A production build is served by the API itself - src/index.ts serves client/build -
// so the page and the API are always on the same origin. A relative base means one
// build works behind any hostname and nothing has to be rebuilt if that hostname
// changes. This used to be a hardcoded https://jookable.herokuapp.com, which no longer
// resolves.
let apiRoot = `/${apiVersion}`;

// Socket.IO connects to an origin, not to the versioned REST path. Left undefined it
// connects to the origin the page was served from, which is what we want in production
// for the same reason the REST base is relative.
let socketOrigin: string = undefined;

if (isDev) {
  // In development Vite serves the page on :3000 and the API runs separately on :5000.
  apiRoot = `http://localhost:5000/${apiVersion}`;
  socketOrigin = 'http://localhost:5000';
}

export const socketRoot = socketOrigin;

export const platform = 'web';

const commonHeaders = { 'Content-Type': 'application/json', platform: platform };

export async function makeApiRequest(apiDomain, path, method, itemPayload, token) {
  let headers: any = commonHeaders;
  if (token) {
    headers = { ...commonHeaders, auth: token };
  }

  const options: any = {
    method: method,
    headers: headers,
    body: itemPayload ? JSON.stringify(itemPayload) : null,
  };

  const response = await fetch(`${apiRoot}/${apiDomain}/${path}`, options);

  // A 401 on sign-in is just a wrong password, and its message has to reach the form. Only a
  // 401 on a request that carried a token means the session is gone. This used to reload
  // the sign-in page with no message at all, which looked like sign-in was broken.
  if (response.status === 401 && token) {
    window.localStorage.clear();
    window.location.href = '/';
    throw new Error('Redirecting to login');
  }

  const text = await response.text();
  let json: { [key: string]: any } = {};

  try {
    json = JSON.parse(text);
  } catch (err) {
    // Not our JSON, so something between here and the server answered instead, such as
    // Cloudflare's error page when the server is down or restarting. That page is a wall
    // of HTML, which is no use to anyone reading it in a search box.
    const error = new Error(unreadableResponseMessage(response.status)) as NetworkError;
    error.status = response.status;
    throw error;
  }

  if (response.status < 200 || response.status >= 300) {
    const message = (json && json.error) || `Invalid Response Code: ${response.status}`;
    const error = new Error(message) as NetworkError;
    error.status = response.status;
    // Sign-in answers an unconfirmed account with a code and its email, so the page can
    // offer to send the link again.
    error.code = json && json.code;
    error.email = json && json.email;
    // With two-step sign-in on, a right password answers MFA_REQUIRED and this token,
    // which is traded with the code from the app at /auth/login/mfa.
    error.mfaToken = json && json.mfaToken;
    throw error;
  }

  return json as any;
}

// For a request whose body is a file rather than JSON, such as a venue logo. The answer is
// JSON as usual, and errors come back the same way as from makeApiRequest.
export async function makeRawApiRequest(
  path: string,
  method: string,
  body: Blob | null,
  contentType: string | null,
  token: string,
) {
  const headers: any = { platform: platform, auth: token };
  if (contentType) {
    headers['Content-Type'] = contentType;
  }
  const response = await fetch(`${apiRoot}/${path}`, { method, headers, body });

  if (response.status === 401) {
    window.localStorage.clear();
    window.location.href = '/';
    throw new Error('Redirecting to login');
  }

  let json: { [key: string]: any } = {};
  try {
    json = JSON.parse(await response.text());
  } catch (err) {
    json = {};
  }
  if (response.status < 200 || response.status >= 300) {
    // A body over the server's limit is refused before it reaches the app, with no JSON.
    const fallback = response.status === 413 ? 'That file is too big.' : `Invalid Response Code: ${response.status}`;
    const error = new Error((json && json.error) || fallback) as NetworkError;
    error.status = response.status;
    throw error;
  }
  return json as any;
}

export function unreadableResponseMessage(status: number): string {
  if (status >= 500) {
    return 'Jookable is having trouble right now. Please try again in a minute.';
  }
  return `Something went wrong (${status}). Please try again.`;
}

export interface NetworkError extends Error {
  status: number;
  code?: string;
  email?: string;
  mfaToken?: string;
}

const api = {
  auth: (apiPath, method, data) => makeApiRequest('auth', apiPath, method, data, null),
  users: (apiPath, method, token, data) => makeApiRequest('user', apiPath, method, data, token),
  players: (apiPath, method, token, data) => makeApiRequest('player', apiPath, method, data, token),
  queueVideos: (apiPath, method, token, data) => makeApiRequest('queueVideo', apiPath, method, data, token),
  admin: (apiPath, method, token, data) => makeApiRequest('admin', apiPath, method, data, token),
  trivia: (apiPath, method, token, data) => makeApiRequest('trivia', apiPath, method, data, token),
  venues: (apiPath, method, token, data) => makeApiRequest('venues', apiPath, method, data, token),
  shoutouts: (apiPath, method, token, data) => makeApiRequest('shoutout', apiPath, method, data, token),
  adminMessages: (apiPath, method, token, data) => makeApiRequest('admin-messages', apiPath, method, data, token),
};

export default api;
